<template>
  <button class="ai-search-submit"
          tabindex="0"
          type="button"
          aria-label="Expert AI"
          @click="handleAction"
          @keydown.enter="handleAction"
  >
    <div class="ai-search-icon__container">
      <img class="ai-search-icon" alt="ai-search-icon">
    </div>
    <div class="ai-search-submit__content-container">
      <div class="ai-search-submit__content">
        <div class="ai-search-submit__text">
          {{ $t('HEADER.EXPERT_CLINICIAN') }}
        </div>
      </div>
    </div>
  </button>
  <LoginRegisterModal ref="loginRegisterModal" />
</template>

<script setup>
import { ref } from 'vue';
import LoginRegisterModal from '_acaSrc/components/login/LoginRegisterModal.vue';
import useExpertAiAction from '_acaSrc/composables/useExpertAiAction.composable';

const props = defineProps({
    clickEventPrefix: String
});

const loginRegisterModal = ref(null);
const { handleExpertAiAction } = useExpertAiAction();

const getCurrentSearchInputValue = () => {
    const searchInput = document.querySelector('#tbSearch');
    return searchInput ? searchInput.value.trim() : '';
};

const handleAction = async(event) => {
    const uiElementName = `${props.clickEventPrefix ?? 'unknown'}-search-ai-mode`;
    const searchTerm = getCurrentSearchInputValue();
    await handleExpertAiAction({
        event,
        searchTerm,
        uiElementName,
        loginRegisterModalRef: loginRegisterModal
    });
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@import (reference) '~_acaAssets/global/global.less';
@import (reference) '~_acaAssets/wkce/mixins.less';

/* stylelint-disable declaration-property-value-no-unknown */

/* stylelint-disable-next-line */
@UTD-AI-BORDER-GRADIENT-COLOR-1: #43B9FF;
@UTD-AI-BORDER-GRADIENT-COLOR-2: #89DF9B;

.ai-search-submit {
  .ds1-flex-vertical-center();
  .ds1-ml-1();
  .ds1-pa-0();
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  
  .ai-search-submit__content-container {
    overflow: hidden;
    border-radius: 0 100px 100px 0;
    height: 100%;
  }
  
  .utd-content-main & {
    .ai-search-icon__container {
      border-radius: 4px 0 0 4px;
    }

    .ai-search-submit__content {
      .ds1-flex-vertical-center();
    }

    .ai-search-submit__text {
      .ds1-pl-compact();
      .ds1-pr-1();
      font-size: 11px;
    }
  }

  .ai-search-submit__content {
    display: none;
    position: relative;
    height: 100%;
    width: 100%;
    pointer-events: none;
    border-radius: inherit;
    border: 1px solid transparent;
    border-left: 0;
    background: @DS1-UTD-GRAY-BG-COLOR content-box;
    background-clip: padding-box;
    box-sizing: border-box;

    &::after {
      --border-width: 2px;
      content: "";
      position: absolute;
      background: linear-gradient(
        0.25turn,
        @UTD-AI-BORDER-GRADIENT-COLOR-1,
        @UTD-AI-BORDER-GRADIENT-COLOR-2
      );
      background-clip: border-box;
      width: calc(100% + var(--border-width));
      height: calc(100% + var(--border-width) * 2);
      border-radius: inherit;
      z-index: -1;
    }
  }

  .ai-search-submit__text {
    .ds1-pl-1();
    .ds1-pr-2();
    display: flex;
    position: relative;
    align-items: center;
    height: 100%;
    font-size: 12px;
    font-family: "Fira Sans", sans-serif;
    font-weight: 600;
    color: @DS1-UTD-TOPIC-TEXT-COLOR;
    box-sizing: border-box;
    z-index: 1;
    
    .wk-header & {
      .ds1-pl-compact();
      border-color: @WKCE-GRAY-TINT3;
      font-size: 11px;
    }
  }

  .ai-search-icon__container {
    position: relative;
    height: 100%;
    aspect-ratio: 1;
    border: 1px solid @WKCE-BLUE;
    border-radius: 4px;
    box-sizing: border-box;
    background-image: linear-gradient(
      224deg,
      var(--gradient-color-1) var(--gradient-pos-1),
      var(--gradient-color-2) var(--gradient-pos-2)
    );
    transition:
      --gradient-color-1 .5s,
      --gradient-color-2 .5s,
      --gradient-pos-1 .5s,
      --gradient-pos-2 .5s;
  }

  .ai-search-icon {
    content: url(/search/ai-search-icon.svg);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
  }

  &:hover {
    background: none;

    .ai-search-icon__container {
      --gradient-color-1: @UTD-AI-BG-IMAGE-HOVER-GRADIENT-COLOR-1;
      --gradient-color-2: @UTD-AI-BG-IMAGE-HOVER-GRADIENT-COLOR-2;
      --gradient-pos-1: @UTD-AI-GRADIENT-HOVER-POS-1;
      --gradient-pos-2: @UTD-AI-GRADIENT-HOVER-POS-2;
    }

    .ai-search-submit__content::after {
      animation: conic-gradient-spin 4s linear infinite;
      scale: 1.9 1.0;
      background: conic-gradient(
          from var(--conic-gradient-angle),
          transparent 15deg,
          @UTD-AI-BORDER-GRADIENT-COLOR-1 60deg,
          @UTD-AI-BORDER-GRADIENT-COLOR-2 120deg,
          @UTD-AI-BORDER-GRADIENT-COLOR-2 240deg,
          @UTD-AI-BG-IMAGE-GRADIENT-COLOR-1 300deg,
          transparent 345deg
        );
    }
  }
}

/* stylelint-disable-next-line order/order */
@media only screen and (min-width: 768px) {
  .ai-search-submit {
    .ai-search-submit__text {
      .ds1-flex-vertical-center();
      .ds1-pr-1();
    }

    .ai-search-icon__container {
      border-radius: 4px 0 0 4px;
    }

    .ai-search-submit__content {
      .ds1-flex-vertical-center();
    }
  }

  .utd-search {
    .ai-search-submit {
      margin-left: 12px;

      .ai-search-submit__text {
        .ds1-flex-vertical-center();
        .ds1-pa-1();
        .ds1-pr-2();
        line-height: 1;
        font-size: 12px;
      }

      .ai-search-icon {
        width: 19px;
      }

      .ai-search-icon__container {
        border-radius: 4px 0 0 4px;
      }

      .ai-search-submit__content {
        .ds1-flex-vertical-center();
        border-width: 2px;
        border-left: 0;
      }
    }
  }
}


</style>